import React, { useEffect, useState } from 'react';
import { Grid, InputLabel, MenuItem } from '@material-ui/core';
import {
  Checkbox,
  FormControlLabel,
  createStyles,
  makeStyles,
  Select,
} from '@material-ui/core';
import Input from '../Input';
import {
  tipoAcidentesList,
  jurisdicaoList,
  simNaoNaoInformado,
  severidade,
  elementosAtingidosList,
  iluminacaoList,
  tempoList,
  localList,
  superficiePistaList,
  tipoCruzamento,
  controleTrafegoList,
  informacaoFontesList,
} from '../../utils/objectsSelect';
import { DriverIncidenteDetailsType } from '../../@types/records';
import { useStyles } from './styles';

interface IProps {
  incidentDetails: DriverIncidenteDetailsType;
  setIncidentDetails: React.Dispatch<React.SetStateAction<DriverIncidenteDetailsType>>;
  isUpdate?: boolean;
  desabilitado?: boolean;
}

const optionStyles = makeStyles(() =>
  createStyles({
    inputContainer: {
      border: '1px solid #0000002b',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: 'white',
      borderRadius: 6,
      padding: '10px 15px',
    },
    formControlLabel: {
      minWidth: 150,
    },
    label: {
      marginTop: 5,
      marginBottom: 8,
      fontFamily: 'Roboto',
      fontSize: 14,
      alignSelf: 'start',
      color: '#000000',
    },
    inputLabel: {
      fontSize: 15,
      marginLeft: 10,
    },
    input: {
      marginLeft: 10,
    },
  }),
);

const isInList = (option: string, list: { label: string }[]): string => {
  const index = list.find((item) => item.label === option);
  return index ? option : 'Não informado';
};

const IncidentDetailsForm: React.FC<IProps> = ({
  incidentDetails,
  setIncidentDetails,
  isUpdate = false,
  desabilitado,
}) => {
  const classes = optionStyles();
  const selectStyles = useStyles();
  const [details, setDetails] = useState<DriverIncidenteDetailsType>(incidentDetails);

  useEffect(() => {
    setDetails(incidentDetails);
  }, [incidentDetails]);

  return (
    <Grid container spacing={3}>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Natureza</InputLabel>
        <Select
          name="Natureza"
          placeholder="Natureza"
          value={isInList(details.Natureza, tipoAcidentesList)}
          onChange={(event) => {
            if (event.target.value === details.Natureza) return null;
            return setDetails({ ...details, Natureza: event.target.value as string });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {tipoAcidentesList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <div className={classes.inputContainer}>
          <label htmlFor="Jurisdição" className={classes.label}>
            Jurisdição
            <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
              {jurisdicaoList.map((value) => (
                <FormControlLabel
                  label={<span className={classes.inputLabel}>{value.label}</span>}
                  className={classes.formControlLabel}
                  key={value.label}
                  control={
                    <Checkbox
                      className={classes.input}
                      color="primary"
                      checked={value.label === details?.Jurisdição}
                      name="Jurisdição"
                      onClick={() => setDetails({ ...details, Jurisdição: value.label })}
                      onBlur={() =>
                        setIncidentDetails({
                          ...incidentDetails,
                          Jurisdição: details.Jurisdição,
                        })
                      }
                      value={value.label}
                      id={value.label}
                      disabled={desabilitado}
                    />
                  }
                />
              ))}
            </div>
          </label>
        </div>
      </Grid>
      <Grid item md={6} sm={12}>
        <div className={classes.inputContainer}>
          <label htmlFor="Interseção" className={classes.label}>
            Interseção
            <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
              {simNaoNaoInformado.map((value) => (
                <FormControlLabel
                  label={<span className={classes.inputLabel}>{value.label}</span>}
                  className={classes.formControlLabel}
                  key={value.label}
                  control={
                    <Checkbox
                      className={classes.input}
                      checked={value.label === details?.Interseção}
                      color="primary"
                      onClick={() => setDetails({ ...details, Interseção: value.label })}
                      onBlur={() =>
                        setIncidentDetails({
                          ...incidentDetails,
                          Interseção: details.Interseção,
                        })
                      }
                      name="Interseção"
                      value={value.label}
                      disabled={desabilitado}
                    />
                  }
                />
              ))}
            </div>
          </label>
        </div>
      </Grid>
      <Grid item md={6} sm={12}>
        <div className={classes.inputContainer}>
          <label htmlFor="Severidade" className={classes.label}>
            Severidade
            <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
              {severidade.map((value) => (
                <FormControlLabel
                  label={<span className={classes.inputLabel}>{value.label}</span>}
                  className={classes.formControlLabel}
                  key={value.label}
                  control={
                    <Checkbox
                      className={classes.input}
                      disabled
                      checked={value.label === details?.Severidade}
                      color="primary"
                      onClick={() => setDetails({ ...details, Severidade: value.label })}
                      onBlur={() =>
                        setIncidentDetails({
                          ...incidentDetails,
                          Severidade: details.Severidade,
                        })
                      }
                      name="Severidade"
                      value={value.label}
                    />
                  }
                  disabled={desabilitado}
                />
              ))}
            </div>
          </label>
        </div>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Elementos Atingidos</InputLabel>
        <Select
          name="Elementos Atingidos"
          placeholder="Elementos Atingidos"
          value={isInList(details['Elementos Atingidas'], elementosAtingidosList)}
          onChange={(event) => {
            if (event.target.value === details['Elementos Atingidas']) return null;
            return setDetails({
              ...details,
              'Elementos Atingidas': event.target.value as string,
            });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {elementosAtingidosList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>
          Primeira fonte de informação
        </InputLabel>

        <Select
          name="Primeira fonte de informação"
          placeholder="Primeira fonte de informação"
          value={isInList(details['Primeira Fonte de Informação'], informacaoFontesList)}
          onChange={(event) => {
            if (event.target.value === details['Primeira Fonte de Informação'])
              return null;
            return setDetails({
              ...details,
              'Primeira Fonte de Informação': event.target.value as string,
            });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {informacaoFontesList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código do Acidente"
          label="Código do Acidente"
          type="text"
          placeholder="Código do Acidente"
          defaultValue={details['Código do Acidente']}
          readOnly={!isUpdate}
          onChange={(event) =>
            setDetails({ ...details, 'Código do Acidente': event.target.value })
          }
          onBlur={() => setIncidentDetails(details)}
          disabled={desabilitado}
        />
      </Grid>
      <Grid item md={6} sm={12}>
        <div className={classes.inputContainer}>
          <label htmlFor="Hora informada" className={classes.label}>
            Hora informada
            <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
              {simNaoNaoInformado.map((value) => (
                <FormControlLabel
                  label={<span className={classes.inputLabel}>{value.label}</span>}
                  className={classes.formControlLabel}
                  key={value.label}
                  control={
                    <Checkbox
                      className={classes.input}
                      checked={value.label === details?.['Hora informada']}
                      color="primary"
                      onClick={() =>
                        setDetails({ ...details, 'Hora informada': value.label })
                      }
                      onBlur={() =>
                        setIncidentDetails({
                          ...incidentDetails,
                          'Hora informada': details?.['Hora informada'],
                        })
                      }
                      name="Hora informada"
                      value={value.label}
                    />
                  }
                  disabled={desabilitado}
                />
              ))}
            </div>
          </label>
        </div>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Iluminação</InputLabel>
        <Select
          name="Iluminação"
          placeholder="Iluminação"
          value={isInList(details.Iluminação, iluminacaoList)}
          onChange={(event) => {
            if (event.target.value === details.Iluminação) return null;
            return setDetails({ ...details, Iluminação: event.target.value as string });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {iluminacaoList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <div className={classes.inputContainer}>
          <label htmlFor="Tempo" className={classes.label}>
            Tempo
            <div style={{ display: 'flex', flexFlow: 'row wrap' }}>
              {tempoList.map((value) => (
                <FormControlLabel
                  label={<span className={classes.inputLabel}>{value.label}</span>}
                  className={classes.formControlLabel}
                  key={value.label}
                  control={
                    <Checkbox
                      className={classes.input}
                      checked={value.label === details?.Tempo}
                      color="primary"
                      onClick={() => setDetails({ ...details, Tempo: value.label })}
                      onBlur={() =>
                        setIncidentDetails({ ...incidentDetails, Tempo: details?.Tempo })
                      }
                      name="Tempo"
                      value={value.label}
                    />
                  }
                  disabled={desabilitado}
                />
              ))}
            </div>
          </label>
        </div>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Local</InputLabel>
        <Select
          name="Local"
          placeholder="Local"
          value={isInList(details.Local, localList)}
          onChange={(event) => {
            if (event.target.value === details.Local) return null;
            return setDetails({ ...details, Local: event.target.value as string });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {localList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Superfície da Pista</InputLabel>
        <Select
          name="Superfície da Pista"
          placeholder="Superfície da Pista"
          value={isInList(details['Superfície da Pista'], superficiePistaList)}
          onChange={(event) => {
            if (event.target.value === details['Superfície da Pista']) return null;
            return setDetails({
              ...details,
              'Superfície da Pista': event.target.value as string,
            });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {superficiePistaList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Tipo de Cruzamento</InputLabel>
        <Select
          name="Tipo de Cruzamento"
          placeholder="Tipo de Cruzamento"
          value={isInList(details['Tipo de Cruzamento'], tipoCruzamento)}
          onChange={(event) => {
            if (event.target.value === details['Tipo de Cruzamento']) return null;
            return setDetails({
              ...details,
              'Tipo de Cruzamento': event.target.value as string,
            });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {tipoCruzamento.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <InputLabel className={selectStyles.label}>Controle de Tráfego</InputLabel>
        <Select
          name="Controle de Tráfego"
          placeholder="Controle de Tráfego"
          value={isInList(details['Controle de Tráfego'], controleTrafegoList)}
          onChange={(event) => {
            if (event.target.value === details['Controle de Tráfego']) return null;
            return setDetails({
              ...details,
              'Controle de Tráfego': event.target.value as string,
            });
          }}
          onBlur={() => setIncidentDetails(details)}
          className={selectStyles.select}
          variant="outlined"
          disabled={desabilitado}
        >
          {controleTrafegoList.map((item) => (
            <MenuItem value={item.label} key={item.label}>
              {item.label}
            </MenuItem>
          ))}
        </Select>
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código AMCBO"
          label="Código AMCBO"
          type="text"
          placeholder="Código AMCBO"
          defaultValue={details['Código AMC'] || details['Código do Acidente']}
          onChange={(event) =>
            setDetails({ ...details, 'Código AMC': event.target.value })
          }
          onBlur={() =>
            setIncidentDetails({
              ...details,
              'Código AMC': details['Código AMC'] || details['Código do Acidente'],
            })
          }
          disabled={desabilitado}
        />
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código PRE"
          label="Código PRE"
          type="text"
          placeholder="Código PRE'"
          defaultValue={details['Código PRE']}
          onChange={(event) =>
            setDetails({ ...details, 'Código PRE': event.target.value })
          }
          onBlur={() => setIncidentDetails(details)}
          disabled={desabilitado}
        />
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código PRF"
          label="Código PRF"
          type="text"
          placeholder="Código PRF"
          defaultValue={details['Código PRF']}
          onChange={(event) =>
            setDetails({ ...details, 'Código PRF': event.target.value })
          }
          onBlur={() => setIncidentDetails(details)}
          disabled={desabilitado}
        />
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código SAMU"
          label="Código SAMU"
          type="text"
          placeholder="Código SAMU"
          defaultValue={details['Código SAMU']}
          onChange={(event) =>
            setDetails({ ...details, 'Código SAMU': event.target.value })
          }
          onBlur={() => setIncidentDetails(details)}
          disabled={desabilitado}
        />
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código PEFOCE"
          label="Código PEFOCE"
          type="text"
          placeholder="Código PEFOCE"
          defaultValue={details['Código PEFOCE']}
          onChange={(event) =>
            setDetails({ ...details, 'Código PEFOCE': event.target.value })
          }
          onBlur={() => setIncidentDetails(details)}
          disabled={desabilitado}
        />
      </Grid>
      <Grid item md={6} sm={12}>
        <Input
          name="Código SIM"
          label="Código SIM"
          type="text"
          placeholder="Código SIM"
          defaultValue={details['Código SIM']}
          onChange={(event) =>
            setDetails({ ...details, 'Código SIM': event.target.value })
          }
          onBlur={() => setIncidentDetails(details)}
          disabled={desabilitado}
        />
      </Grid>
    </Grid>
  );
};

export default IncidentDetailsForm;
